import { LogHelpers } from '../../service/LogHelpers/LogHelpers';
import { CampaignsCalcTypeFilter } from '../../typings/domain';
import { CalcType, OutputCategory } from '../../typings/graphql-log';
import { GridData } from '../../typings/grid';
import {
    Category,
    DisplayGroups,
    LogAdsEntity,
    LogCampaignEntity,
    LogEntry,
    LogEventCategories,
    LogSourceRow,
    LogSourceRowEvent,
    LogTypenames,
    NullableString
} from '../../typings/log';
import { ClientFeatures, Regions } from '../../typings/state';
import { filterArrayByNullAndUndefined, mapStringToDate } from '../common/common';

import { prepareBroadmatchGroups } from './broadmatch/broadmatch';
import {
    prepareInlineDisplay,
    prepareNetwork,
    prepareRegionsGroups,
    prepareStatusData,
    prepareValueChangeEvent
} from './common/common';
import {
    prepareDemographyGroups,
    prepareRetargetingGroups,
    prepareSimpleMultipliersGroups
} from './multipliers/multipliers';
import { prepareRestrictFrequency } from './restrictfrequency/restrictfrequency';
import { prepareStrategy } from './strategy/strategy';
import { prepareTimeTargetingGroups } from './timetargeting/timetargeting';

function mapCategory(category: OutputCategory): Category {
    switch (category) {
        case OutputCategory.CAMPAIGN_CREATION:
            return LogEventCategories.campaignStatus;
        case OutputCategory.CAMPAIGN_ARCHIVED:
        case OutputCategory.CAMPAIGN_UNARCHIVED:
        case OutputCategory.CAMPAIGN_HIDE:
        case OutputCategory.CAMPAIGN_SHOW:
            return LogEventCategories.campaignStatus;
        case OutputCategory.BANNERS_SHOW:
        case OutputCategory.BANNERS_HIDE:
        case OutputCategory.BANNERS_UNARCHIVED:
        case OutputCategory.BANNERS_ARCHIVED:
            return LogEventCategories.bannerStatus;
        default:
            return category;
    }
}

function prepareDiff(
    category: OutputCategory,
    event: LogSourceRowEvent,
    regions: Regions,
    features: ClientFeatures
): DisplayGroups {
    switch (event.__typename) {
        case LogTypenames.campaignStatus:
        case LogTypenames.bannerEvent:
            return prepareStatusData(category);
        case LogTypenames.valueChange:
            return prepareValueChangeEvent(category, event);
        case LogTypenames.timeTargeting:
            return prepareTimeTargetingGroups(event.oldTimeTarget, event.newTimeTarget);
        case LogTypenames.broadmatch:
            return prepareBroadmatchGroups(event.oldBroadMatch, event.newBroadMatch);
        case LogTypenames.listChange:
            return prepareInlineDisplay(event.oldList, event.newList);
        case LogTypenames.network:
            return prepareNetwork(event.oldNetwork, event.newNetwork);
        case LogTypenames.regions:
        case LogTypenames.adgroupRegions:
            return prepareRegionsGroups(regions, event.oldRegions, event.newRegions);
        case LogTypenames.retargeting:
            return prepareRetargetingGroups(event.oldRetargetingMultipliers, event.newRetargetingMultipliers);
        case LogTypenames.demography:
            return prepareDemographyGroups(event.oldDemographyMultipliers, event.newDemographyMultipliers);
        case LogTypenames.simpleMultipliers:
            return prepareSimpleMultipliersGroups(event.category, event.oldMultiplier, event.newMultiplier);
        case LogTypenames.strategy:
            return prepareStrategy(features, event.oldStrategy, event.newStrategy, event.currencyCode);
        case LogTypenames.restrictFrequency:
            return prepareRestrictFrequency(event.oldRestrictFrequency, event.newRestrictFrequency);
        default:
            throw Error(`Не поддерживается тип ${event.__typename}`);
    }
}

function calcUserLogin(row: LogSourceRow): string | undefined {
    const { user } = row;
    if (user === null || user.uid === null) {
        return undefined;
    }
    return user.uid.toString() === '-1' ? LogHelpers.getLocalizedString('yandex') : user.login;
}

function mapEntityFromApiFormat(
    data: { id: NullableString; name?: string; title?: string } | null
): LogAdsEntity | undefined {
    if (data === null || data.id === null) {
        return undefined;
    }
    const { id, name, title } = data;
    return {
        id,
        name: name || title || '–'
    };
}

function mapEntitiesFromApiFormat(
    data: Array<{ id: NullableString; name?: string; title?: string } | null>
): Array<LogAdsEntity> {
    return filterArrayByNullAndUndefined<{ id: string; name: string }>(data.map(mapEntityFromApiFormat));
}

function mapEventCampaignFromApiFormat(campaign: { id: string; name: string; calcType: CalcType }): LogCampaignEntity {
    return {
        id: campaign.id,
        name: campaign.name,
        calcType: CampaignsCalcTypeFilter[campaign.calcType]
    };
}

function mapLogRowToLogEntryWithHeights(
    row: LogSourceRow,
    index: number,
    total: number,
    regions: Regions,
    features: ClientFeatures
): { logEntry: LogEntry; canExpand: boolean } {
    const { event } = row,
        category = event.category,
        diff = prepareDiff(category, event, regions, features),
        newIndex = total + index,
        userName = calcUserLogin(row),
        banners = mapEntitiesFromApiFormat(row.event.ads || []),
        groups = mapEntitiesFromApiFormat(event.adGroup ? [event.adGroup] : []),
        canExpand = diff.needExpand || banners.length > 1 || groups.length > 1;

    return {
        canExpand: canExpand,
        logEntry: {
            id: String(newIndex),
            index: newIndex,
            date: mapStringToDate(row.datetime),
            userName: userName,
            campaign: mapEventCampaignFromApiFormat(event.campaign),
            bannersNo: banners,
            category: category,
            changedValueType: event.__typename as LogTypenames,
            changedFieldId: mapCategory(category),
            changedValue: diff,
            tool: row.changeSource,
            groupsNo: groups
        }
    };
}

export function mapSourceToData(
    source: Array<LogSourceRow>,
    total: number,
    regions: Regions,
    features: ClientFeatures
): {
    gridData: GridData;
    rowsCanExpand: Map<number, boolean>;
} {
    // Фильтруем события с неизвестным __typename
    // при добавлении нового события бэкэнд начинает присылать его сразу,
    // при этом event у него оказывается пустым.
    const logTypes: Array<String> = Object.keys(LogTypenames).map(key => LogTypenames[key]);
    const filtredSource: Array<LogSourceRow> = source.filter(row => logTypes.includes(row.event.__typename));

    const entries = filtredSource.reduce(
        (
            res: {
                logEntries: Array<LogEntry>;
                rowsCanExpand: Map<number, boolean>;
            },
            sourceRow: LogSourceRow,
            index: number
        ) => {
            const entry = mapLogRowToLogEntryWithHeights(sourceRow, index, total, regions, features);
            if (entry === null) {
                return res;
            }
            res.logEntries.push(entry.logEntry);
            res.rowsCanExpand.set(entry.logEntry.index, entry.canExpand);

            return res;
        },
        {
            logEntries: [],
            rowsCanExpand: new Map<number, boolean>()
        }
    );

    return {
        gridData: {
            data: entries.logEntries,
            total: entries.logEntries.length,
            allItemsIds: []
        },
        rowsCanExpand: entries.rowsCanExpand
    };
}
